import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import {Button, Label, TextPlain, Title, TitleH2, TitleH3, TitleH4} from "components/generic";
import {
  ContainerRedirect,
  ContainerSearch,
  ContainerValueView,
  ContainerView,
  DialogWrapper,
  FlexBox,
} from "components/containers";
import {AboutMeView, PersonalDetails, SocialPreferences, TitleEdit, TitleProfile,} from "./SimpleComponents";
import {useRange} from "components/Range";
import {API_USERS, HOST_ROOMS_ROUTE, MONTHS, STUDENT_ROOMS_ROUTE} from "utils/constants";

import {useContextHospi} from "context/ContextHospi";
import {MenuLeftSide} from "components/MenuLeftSide";
import {LoginDetails} from "./LoginDetails";
import {ICityOfRecidence, IUserShot} from "interfaces/intarfaces";
import {MainWrapper} from "pages/HostHouseRegistration/styles";
import {UserAPI} from "../../API/userApi";
import Modal from "../../components/Modal";
import {StudentAboutInfo} from "./StudentAboutInfo";
import useSizeWindow from "../../utils/hooks/useWindowSize";
import {useNavigate} from "react-router-dom";
import {MainSide} from "../ProfileUserEdit/styles";
import {Auth} from "aws-amplify";
import {LoaderCustom} from "../../components/Loader";
import {theme} from "../../styles/theme";
import {VerificationBanner} from "../../components/VerificationBanner";

export const ProfileUserView: React.FC = () => {
  const { userProfile, removeUserAccount, setUser } = useContextHospi();
  const { t, i18n } = useTranslation(
    userProfile?.user_type && userProfile?.user_type === "student"
      ? "student"
      : "host"
  );
  const { t: t_calendar } = useTranslation();
  const [userInfo, setUserInfo] = useState<Partial<IUserShot>>({});
  const [showModal, setShowModal] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState<number>(0);
  const [deleteUserAccount, setDeleteUserAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentUserID, setCurrentUserID] = useState(
      userProfile?.user_id || ""
  );
  const privacy = useRange(userProfile?.privacy || 50);
  const device = useSizeWindow();
  const navigate = useNavigate();
  const newInTown = useRange(
    (userProfile?.user_type && userProfile?.user_type === "student"
      ? userProfile?.autonomy
      : userProfile?.requested_autonomy) || 50
  );

  useEffect(() => {
    const getID = async () => {
      const { attributes } = await Auth.currentAuthenticatedUser();
      setCurrentUserID(attributes.sub ?? "");
    };
    getID();
  }, [userProfile]);

  const dateLine = (value: string | undefined): string => {
    if (value === undefined || value === null) return "";
    const dt = new Date(value);
    return `${dt.getDate()} ${t_calendar(
      `calendar.months.${MONTHS[dt.getMonth()]}`
    ).toLocaleLowerCase()} ${dt.getFullYear()}`;
  };

  const cityWhereWillGo = (x: ICityOfRecidence | undefined | null) => {
    if (x) return `${x.city}, ${x.province}, ${x.country}`;
    return "";
  };

  const toRooms = async () => {
    navigate(
        userProfile?.user_type === "host" ? `/${i18n.language}${HOST_ROOMS_ROUTE.slice(5)}` : `/${i18n.language}${STUDENT_ROOMS_ROUTE.slice(5)}`
    );
  };

  const fetchUser = () => {
    if (userProfile?.user_id)
      UserAPI.get(API_USERS + userProfile.user_id + "/user-preview").then((response) => {
        setUserInfo(response as Partial<IUserShot>);
        setShowModal(2);
      });
  };

  const deleteUser = async () => {
    setLoading(true);
    setShowModal(0);
    const response = await UserAPI.delete(API_USERS + `${currentUserID}`);
    setLoading(false);
    if (response.hasOwnProperty("success") && response["success"]) {
      setUser(undefined);
      setDeleteUserAccount(true);
      navigate(`/${i18n.language}/`);
    }
    navigate(`/${i18n.language}/`);
  };

  if (deleteUserAccount)
    return (
        <ContainerView>
          <MainSide>
            {loading && (
                <LoaderCustom
                    size={5}
                    color={theme.colors.bgBody}
                />
            )}
            <FlexBox direction="column">
              <ContainerRedirect>
                <Title m="0 0 24px 0">{t("deleteAccount.title")}</Title>
                <Label lineHeight="150%" p="0">
                  {t("deleteAccount.text")}
                </Label>
              </ContainerRedirect>
              <Button
                  main
                  fontWeight="400"
                  w="100%"
                  m="24px 0 0 0"
                  onClick={() => removeUserAccount()}
                  style={{ maxWidth: "336px" }}
              >
                {t("deleteAccount.button")}
              </Button>
            </FlexBox>
          </MainSide>
        </ContainerView>
    );

  const genderLabel = {
    male: t(
      `${userProfile?.user_type}Profile.aboutMeInfo.form.gender.itemRadio.0`
    ),
    female: t(
      `${userProfile?.user_type}Profile.aboutMeInfo.form.gender.itemRadio.1`
    ),
    prefer_not_answer: t(
      `${userProfile?.user_type}Profile.aboutMeInfo.form.gender.itemRadio.2`
    ),
  };

  return (
    <ContainerView>
      <MenuLeftSide/>
      <MainWrapper>
        <TitleProfile user={userProfile?.user_type!} fetchUser={fetchUser} title={t("viewTitle")} edit="false" />
        <PersonalDetails
          first_name={userProfile?.first_name}
          last_name={userProfile?.last_name}
          birth_date={
            userProfile?.user_type && userProfile?.user_type === "student"
              ? dateLine(userProfile?.birth_date)
              : undefined
          }
          birth_country={
            userProfile?.user_type && userProfile?.user_type === "student"
              ? userProfile?.birth_country
              : undefined
          }
          phone_code={userProfile?.phone_code}
          phone_number={userProfile?.phone_number}
          thumbnail_url={userProfile?.thumbnail_url}
        />
        {userProfile?.user_type && userProfile?.user_type === "student" && (
          <ContainerValueView style={{ marginBottom: 0 }}>
            <TitleEdit keyItem="searchCriteria" />
            <ContainerSearch>
              <div>
                <TitleH3>
                  {t(`studentProfile.searchCriteria.form.moveInDate.title`)}
                </TitleH3>
                <TitleH4>
                  {dateLine(userProfile?.preferred_move_in || undefined)}
                </TitleH4>
              </div>
              <div>
                <TitleH3>
                  {t(`studentProfile.searchCriteria.form.moveOutDate.title`)}
                </TitleH3>
                <TitleH4>
                  {dateLine(userProfile?.preferred_move_out || undefined)}
                </TitleH4>
              </div>
              <div>
                <TitleH3>
                  {t(`studentProfile.searchCriteria.form.budget.title`)}
                </TitleH3>
                <TitleH4>{`€ ${userProfile?.max_budget || "-"}`}</TitleH4>
              </div>
              <div>
                <TitleH3>
                  {t(`studentProfile.searchCriteria.form.city.title`)}
                </TitleH3>
                <TitleH4>
                  {userProfile?.city_of_residence &&
                    `${cityWhereWillGo(userProfile?.city_of_residence)}`}
                </TitleH4>
              </div>
            </ContainerSearch>
          </ContainerValueView>
        )}
        <ContainerValueView style={{ marginBottom: 0 }}>
          <TitleEdit keyItem="aboutMeInfo" />
          <AboutMeView
            genderUser={genderLabel[userProfile?.gender!]}
            studyStudent={
              userProfile?.user_type === "student"
                ? userProfile?.study
                : undefined
            }
            spokenLanguage={userProfile?.spoken_languages}
            expirenceStudent={
              userProfile?.user_type === "student"
                ? userProfile?.past_experience
                : undefined
            }
            descriptionUser={userProfile?.description}
          />
        </ContainerValueView>
        <ContainerValueView style={{ marginBottom: 0 }}>
          <TitleEdit keyItem="socialPreferences" />
          <SocialPreferences
            roleUser={userProfile?.user_type === "student" ? "student" : "host"}
            privacy={privacy}
            newInTown={newInTown}
          />
        </ContainerValueView>
        <LoginDetails />
        <FlexBox
            justify="space-between"
            margin="0 0 0px 0"
            width="100%"
            className="button-group"
        >
          <FlexBox justify="flex-start" margin="0 0">
            <Button outline onClick={() => setShowDeleteModal(1)}>
              {device === "mobile"
                  ? "Delete"
                  : t(`${userProfile?.user_type}Profile.buttonRemove.title`)}
            </Button>
          </FlexBox>
          <FlexBox justify="flex-end" margin="0 0">
            <Button
                main
                onClick={() => toRooms()}
                m="0 0 0 24px"
            >
              {t(`${userProfile?.user_type}Profile.buttonContinue.title`)}
            </Button>
          </FlexBox>
        </FlexBox>
      </MainWrapper>
      {!!showModal && (
        <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
          {showModal === 2 && userInfo && (
            <DialogWrapper style={{ gridTemplateRows: "repeat(2, auto)" }}>
              <StudentAboutInfo
                isPreviewMode
                {...userInfo}
                closeBtn={() => setShowModal(0)}
              />
            </DialogWrapper>
          )}
        </Modal>
      )}
      {showDeleteModal !== 0 && (
          <Modal isShow={!!showDeleteModal} handleClose={() => setShowDeleteModal(0)}>
            <DialogWrapper>
              <TitleH2>{t(`${userProfile?.user_type}Profile.deleteAccount.title`)}</TitleH2>
              <TextPlain
                  style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
              >
                {t(`${userProfile?.user_type}Profile.deleteAccount.text`, {
                  joinArrays: "\n",
                })}
              </TextPlain>
              <div className="buttons">
                <Button normal onClick={() => setShowDeleteModal(0)}>
                  {t(`${userProfile?.user_type}Profile.deleteAccount.buttonKeep`)}
                </Button>
                <Button danger onClick={deleteUser}>
                  {t(`${userProfile?.user_type}Profile.deleteAccount.buttonDelete`)}
                </Button>
              </div>
            </DialogWrapper>
          </Modal>)}
    </ContainerView>
  );
};

